import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { FC, useState } from 'react';
import { useAdvancedFiltersContext } from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { AutoCompleteSearchField } from '../../autocomplete/AutoCompleteSearchField';
import { EntityPropertySearchTag, IEntityPropertySearch } from '../../entities/EntityPropertySearchTag';
import { useTagsStorage } from '../../entities/tags/TagsEditor/hooks';
import { FeaturedAndRecentTags } from '../../entities/tags/TagsEditor/presentation';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { navigation } from '../../../styles/colors';
import { styleSheet } from './styles';

interface CompanyTagFiltersPanelProps {
	className?: string;
	styles?: (StyleDeclarationValue | false)[];
	disabled?: boolean;
}

export const CompanyTagFiltersPanel: FC<CompanyTagFiltersPanelProps> = ({
	className,
	styles = [],
	disabled = false,
}) => {
	const { companyTagFilters, impersonationContext } = useAdvancedFiltersContext();
	const { companyTag, setCompanyTag, clearCompanyTagFilters } = companyTagFilters;

	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const { recentSearches, updateTagsRecentSearches } = useTagsStorage();

	const handleTagSelected = (tag: string) => {
		if (disabled) return;
		updateTagsRecentSearches(tag);
		setCompanyTag(tag);
		setInputValue('');
	};

	const handleRemoveTag = () => {
		if (disabled) return;
		clearCompanyTagFilters();
	};

	const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		setInputValue(e.target.value);
	};

	const emptyResults =
		recentSearches?.length > 0 ? (
			<FeaturedAndRecentTags
				onSelected={(tag: Api.IAccountTag) => handleTagSelected(tag?.name)}
				showNoResults={Boolean(inputValue)}
				impersonationContext={impersonationContext}
			/>
		) : undefined;

	// If a company tag is selected, show it with a remove button
	if (companyTag) {
		const search: IEntityPropertySearch = { value: companyTag };
		return (
			<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
				<div className={css(styleSheet.fieldContainer)}>
					<div className={css(styleSheet.selectedTagContainer)}>
						<EntityPropertySearchTag
							className={css(styleSheet.selectedTag)}
							search={search}
							onRemoveSearch={handleRemoveTag}
						/>
					</div>
				</div>
			</div>
		);
	}

	// Show the autocomplete search when no tag is selected
	return (
		<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
			<div className={css(styleSheet.fieldContainer)}>
				<AutoCompleteSearchField
					anchorClassName={css(styleSheet.searchBox)}
					clearSearchFieldAfterSelectingItem={true}
					dropdownContentClassName={css(styleSheet.autocompleteBox)}
					inputId='companyTagFiltersPanelInput'
					inputProps={{
						placeholder: 'Search company tags',
						onChange: handleValueChange,
						onFocus: () => setIsOpen(true),
						onBlur: () => setIsOpen(false),
						disabled,
					}}
					leftAccessory={
						<span className={css(styleSheet.searchIcon)}>
							<SearchIcon fillColor={navigation} />
						</span>
					}
					onItemSelected={handleTagSelected}
					resultsLimit={25}
					pageSize={25}
					type={Api.ResourceAutoCompleteViewModelType.Tag}
					openOnFocus={Boolean(emptyResults)}
					isOpen={Boolean(emptyResults) || isOpen}
					emptyResults={emptyResults}
					sectionTitleElement={
						<div className={css(styleSheet.sectionWrap)}>
							<p className={css(styleSheet.sectionTitle)}>Top Results</p>
						</div>
					}
				/>
			</div>
		</div>
	);
};
